import * as React from 'react';

import {
  enumSalesOrderStatus,
  Weight,
} from '@rematter/frontend-lib-api-client';
import { Button, DialogContent, DialogOverlay } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useSetAtom } from 'jotai';

import { $updateLoad } from '~/modules/trade/_state/loadDetail.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';
import { SelectSalesOrder } from '~/shared/components/select-fields/SelectSalesOrder';

type Props = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  loadId: string;
  currentSalesOrderId?: string | null;
  heavyWeight?: Weight | null;
  lightWeight?: Weight | null;
  onSuccess?: () => void;
  isUnassigned?: boolean;
};

export const LoadAssignSalesOrderPopover: React.FC<Props> = ({
  isOpen,
  onOpenChange,
  loadId,
  currentSalesOrderId,
  heavyWeight,
  lightWeight,
  onSuccess,
  // isUnassigned,
}) => {
  const t = useTranslations();
  const toast = useToast();
  const [selectedSalesOrderId, setSelectedSalesOrderId] = React.useState<
    string | null
  >(currentSalesOrderId ?? null);
  const [isLoading, setIsLoading] = React.useState(false);

  const updateLoad = useSetAtom($updateLoad);

  const handleSalesOrderChange = React.useCallback(
    (value: string | undefined) => {
      setSelectedSalesOrderId(value as string | null);
    },
    [],
  );

  const handleAssign = React.useCallback(async () => {
    try {
      setIsLoading(true);
      await updateLoad({
        id: loadId,
        salesOrderId: selectedSalesOrderId ?? undefined,
        heavyWeight,
        lightWeight,
      });

      const isAssigning = !currentSalesOrderId && selectedSalesOrderId;
      const isReassigning = currentSalesOrderId && selectedSalesOrderId;
      const isRemoving = currentSalesOrderId && !selectedSalesOrderId;

      if (isAssigning) {
        toast.success(t.loadDetail.messages.salesOrderAssigned);
      } else if (isReassigning) {
        toast.success(t.loadDetail.messages.salesOrderReassigned);
      } else if (isRemoving) {
        toast.success(t.loadDetail.messages.salesOrderRemoved);
      }

      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t.loadDetail.messages.salesOrderAssignmentError,
      );
    } finally {
      setIsLoading(false);
    }
  }, [
    loadId,
    selectedSalesOrderId,
    currentSalesOrderId,
    updateLoad,
    onSuccess,
    onOpenChange,
    t,
    toast,
    heavyWeight,
    lightWeight,
  ]);

  const handleCancel = React.useCallback(() => {
    setSelectedSalesOrderId(currentSalesOrderId ?? null);
    onOpenChange(false);
  }, [currentSalesOrderId, onOpenChange]);

  const isAssigning = !currentSalesOrderId;
  const hasChanged = selectedSalesOrderId !== currentSalesOrderId;

  return (
    <DialogOverlay
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      className="@LoadAssignSalesOrderPopover"
    >
      <DialogContent className="max-w-md">
        <div className="p-6">
          <h2 className="mb-4 text-lg font-semibold">
            {isAssigning
              ? t.loadDetail.actions.assignSO
              : t.loadDetail.actions.reassignSO}
          </h2>

          <div className="mb-6">
            <SelectSalesOrder
              value={selectedSalesOrderId ?? undefined}
              onChange={handleSalesOrderChange}
              label={t.loadDetail.form.salesOrder}
              statuses={[enumSalesOrderStatus.Open]}
            />
          </div>

          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onPress={handleCancel}
              isDisabled={isLoading}
            >
              {t.loadDetail.actions.cancel}
            </Button>
            <Button
              variant="solid"
              color="primary"
              onPress={handleAssign}
              isDisabled={!hasChanged || isLoading}
              isLoading={isLoading}
            >
              {isAssigning
                ? t.loadDetail.actions.assign
                : t.loadDetail.actions.reassign}
            </Button>
          </div>
        </div>
      </DialogContent>
    </DialogOverlay>
  );
};
